import { motion } from 'framer-motion';

// Import downloaded SVG logos
import openaiLogo from '@/assets/ai-logos/openai.svg';
import anthropicLogo from '@/assets/ai-logos/anthropic.svg';
import geminiLogo from '@/assets/ai-logos/gemini.svg';
import xaiLogo from '@/assets/ai-logos/xai.svg';
import perplexityLogo from '@/assets/ai-logos/perplexity.svg';
import metaLogo from '@/assets/ai-logos/meta.svg';
import huggingfaceLogo from '@/assets/ai-logos/huggingface.svg';
import replitLogo from '@/assets/ai-logos/replit.svg';
import googleLogo from '@/assets/ai-logos/google.svg';
import awsLogo from '@/assets/ai-logos/aws.svg';

interface AITool {
  name: string;
  sub: string;
  logo?: string;
  color?: string;
}

const aiTools: AITool[] = [
  { name: "ChatGPT", sub: "OpenAI", logo: openaiLogo },
  { name: "Claude", sub: "Anthropic", logo: anthropicLogo },
  { name: "Gemini", sub: "Google", logo: geminiLogo },
  { name: "Grok", sub: "xAI", logo: xaiLogo },
  { name: "Sora", sub: "OpenAI", logo: openaiLogo },
  { name: "Perplexity", sub: "AI Search", logo: perplexityLogo },
  { name: "AWS Bedrock", sub: "Amazon", logo: awsLogo },
  { name: "LLaMA", sub: "Meta", logo: metaLogo },
  { name: "DALL·E", sub: "OpenAI", logo: openaiLogo },
  { name: "Hugging Face", sub: "Models", logo: huggingfaceLogo },
  { name: "DeepMind", sub: "Google", logo: googleLogo },
  { name: "Replit AI", sub: "Code", logo: replitLogo },
  { name: "Codex", sub: "OpenAI", logo: openaiLogo },
];

const ToolLogo = ({ tool }: { tool: AITool }) => {
  if (tool.logo) {
    return (
      <div className="w-7 h-7 rounded-lg bg-muted/50 flex items-center justify-center p-1 flex-shrink-0">
        <img
          src={tool.logo}
          alt={tool.name}
          className="w-5 h-5 object-contain dark:invert"
        />
      </div>
    );
  }

  return (
    <div
      className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
      style={{ backgroundColor: tool.color || 'hsl(var(--primary))' }}
    >
      {tool.name.charAt(0)}
    </div>
  );
};

export const AIToolsMarquee = () => {
  const doubled = [...aiTools, ...aiTools];

  return (
    <div className="w-full overflow-hidden py-6">
      <p className="text-center text-xs font-medium text-muted-foreground/60 uppercase tracking-[0.2em] mb-5">
        Tools & platforms covered in our workflows
      </p>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-background to-transparent z-10" />

        <motion.div
          className="flex gap-4 w-max"
          animate={{ x: ["0%", "-50%"] }}
          transition={{
            x: { duration: 35, repeat: Infinity, ease: "linear" },
          }}
        >
          {doubled.map((tool, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-border/60 bg-card/50 backdrop-blur-sm flex-shrink-0"
            >
              <ToolLogo tool={tool} />
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-foreground whitespace-nowrap leading-tight">
                  {tool.name}
                </span>
                <span className="text-[10px] text-muted-foreground/70 leading-tight">
                  {tool.sub}
                </span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default AIToolsMarquee;
